import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import YupPassword from 'yup-password';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog, DialogContent, DialogTitle, Button, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { FormInput, ModalTitle } from 'components';
import { useApiRequests } from 'hooks';

import styles from './StaffPasswordResetDialog.module.scss';

YupPassword(Yup);

type Props = {
  clinicUserId: string;
  staffName: string;
  onClose(): void;
};

type FormValues = {
  password: string;
  passwordConfirmed: string;
};

const StaffPasswordResetDialog = ({ clinicUserId, staffName, onClose }: Props) => {
  const { t } = useTranslation('settings');
  const { enqueueSnackbar } = useSnackbar();
  const { resetStaffPassword } = useApiRequests();

  const [submitting, setSubmitting] = useState(false);
  const resetPasswordSchema = useMemo(
    () =>
      Yup.object().shape({
        password: Yup.string()
          .min(8, t('passwordPopup.passwordMinRequirements'))
          .minLowercase(1, t('passwordPopup.passwordMinLowercase'))
          .minUppercase(1, t('passwordPopup.passwordMinUppercase'))
          .minNumbers(1, t('passwordPopup.passwordMinNumbers'))
          .minSymbols(1, t('passwordPopup.passwordMinSymbols'))
          .required(t('passwordPopup.passwordRequired')),
        passwordConfirmed: Yup.string().oneOf([Yup.ref('password')], t('passwordPopup.passwordsMustMatch')),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: yupResolver(resetPasswordSchema),
    mode: 'all',
    defaultValues: {},
  });

  const handleSubmit = async (): Promise<void> => {
    if (!form.formState.isValid) {
      return;
    }

    try {
      setSubmitting(true);

      const values = form.getValues();

      await resetStaffPassword({
        clinicUserId,
        newPassword: values.password,
        confirmPassword: values.passwordConfirmed,
      });

      enqueueSnackbar(t('staffPasswordReset.resetPasswordSuccessfully'), { variant: 'success' });
      onClose();
    } catch (e: any) {
      enqueueSnackbar(e.message ?? t('staffPasswordReset.failedToResetPassword'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: '400px',
          width: '100%',
        },
      }}
    >
      <DialogTitle>
        <ModalTitle title={t('staffPasswordReset.resetPasswordTitle')} onClose={onClose} />
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} className={styles.form}>
          <Typography variant='body2'>
            {t('staffPasswordReset.resetPasswordFor')} <b>{staffName}</b>
          </Typography>

          <Typography variant='body2'>{t('staffPasswordReset.passwordRequirements')}</Typography>

          <Stack className={styles.row}>
            <Typography component='p' variant='body2'>
              {t('staffPasswordReset.passwordLabel')} <span className={styles.asterisk}>*</span>
            </Typography>

            <FormInput name='password' control={form.control} type='password' />
          </Stack>

          <Stack className={styles.row}>
            <Typography component='p' variant='body2'>
              {t('staffPasswordReset.confirmPasswordLabel')} <span className={styles.asterisk}>*</span>
            </Typography>

            <FormInput name='passwordConfirmed' control={form.control} type='password' />
          </Stack>

          <Stack direction='row' spacing='1rem'>
            <Button variant='outlined' fullWidth onClick={onClose} disabled={submitting}>
              {t('manageAccess.cancel')}
            </Button>

            <Button
              variant='contained'
              fullWidth
              onClick={handleSubmit}
              disabled={!form.formState.isValid || submitting}
            >
              {t('staffPasswordReset.confirm')}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default StaffPasswordResetDialog;
